// src/components/RussiaMap.tsx

import { useAuthStore } from "@/store/authStore";
import { db, storage } from "@/lib/firebase";
import { collection, onSnapshot, doc, setDoc } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import * as XLSX from "xlsx";
import { useState, useEffect, useMemo, useRef } from "react";

import { MapContainer } from "./russia-map/MapContainer.tsx";
import { RegionInfoPanel } from "./russia-map/RegionInfoPanel.tsx";
import { StatsCards } from "./russia-map/StatsCards.tsx";
import { EditRegionModal } from "./russia-map/EditRegionModal.tsx";
import { ExcelImportButton } from "./russia-map/ExcelImportButton.tsx";

import {
  getIsoCode,
  regionsData,
  RegionInfo,
} from "@/lib/russiaRegions";

interface RegionData {
  leaderName?: string;
  phone?: string;
  tg?: string;
  birthDate?: string;
  appointDate?: string;
  legalDate?: string;
  deptDate?: string;
  volunteers?: number;
  studentTeams?: number;
  localDepts?: number;
  humanMission?: number;
  training?: number;
  emergency?: number;
  certified?: number;
  photoUrl?: string;
}

const formatDate = (dateInput: any): string => {
  if (!dateInput) return "—";

  if (typeof dateInput === "string") {
    const parts = dateInput.split("-");
    if (parts.length === 3) {
      const [y, m, d] = parts;
      return `${d}.${m}.${y}`;
    }
  }

  if (typeof dateInput === "number") {
    const d = XLSX.SSF.parse_date_code(dateInput);
    if (d) {
      return `${String(d.d).padStart(2, "0")}.${String(d.m).padStart(2, "0")}.${d.y}`;
    }
  }

  if (dateInput instanceof Date) {
    const y = dateInput.getFullYear();
    const m = String(dateInput.getMonth() + 1).padStart(2, "0");
    const d = String(dateInput.getDate()).padStart(2, "0");
    return `${d}.${m}.${y}`;
  }

  return "—";
};

export default function RussiaMap() {
  const [hoveredRegion, setHoveredRegion] = useState<RegionInfo | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<RegionInfo | null>(null);
  const [regionData, setRegionData] = useState<Record<string, RegionData>>({});
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentEditingRegion, setCurrentEditingRegion] = useState<string | null>(null);
  const [activeCards, setActiveCards] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState({ scale: 1.35, translateX: 0, translateY: 0 });
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Callback только для зума
  const triggerZoom = useRef<(svgId: string) => void>(() => {});

  const { user } = useAuthStore();
  const canEditRegion = user?.isAdmin || [4, 6].includes(Number(user?.id));
  const isPavlik = user?.name?.includes("Павлик") ?? false;

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "regions"),
      (snapshot) => {
        const data: Record<string, RegionData> = {};
        snapshot.forEach((docSnap) => {
          data[docSnap.id] = docSnap.data() as RegionData;
        });
        setRegionData(data);
        setLoading(false);
      },
      (err) => {
        console.error("Firestore error:", err);
        setError("Ошибка загрузки данных");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleMouseEnter = (svgId: string) => {
    if (selectedRegion) {
      setHoveredRegion(null);
      return;
    }
    const isoCode = getIsoCode(svgId);
    const info = regionsData[isoCode];
    if (info) setHoveredRegion(info);
  };

  const handleMouseLeave = () => {
    if (selectedRegion) {
      setHoveredRegion(null);
      return;
    }
    setHoveredRegion(null);
  };

  const handleClick = (svgId: string) => {
    const isoCode = getIsoCode(svgId);
    const info = regionsData[isoCode];
    if (info) {
      console.log("Карточка зафиксирована на регионе:", info.name);
      setSelectedRegion(info);
      setHoveredRegion(null);
    }
  };

  const resetZoom = () => {
    setZoom({ scale: 1.35, translateX: 0, translateY: 0 });
    setSelectedRegion(null);
    setHoveredRegion(null);
  };

  const onBackgroundClick = () => {
    resetZoom();
  };

  const stats = useMemo(() => {
    let regionsHuman = 0;
    let totalHuman = 0;
    let regionsTraining = 0;
    let totalTraining = 0;
    let regionsEmergency = 0;
    let totalEmergency = 0;
    let regionsSSO = 0;
    let totalSSO = 0;
    let regionsMO = 0;
    let totalMO = 0;
    let regionsCertified = 0;
    let totalCertified = 0;

    Object.entries(regionData).forEach(([isoCode, data]) => {
      const human = Number(data.humanMission) || 0;
      if (human > 0) { regionsHuman++; totalHuman += human; }

      const training = Number(data.training) || 0;
      if (training > 0) { regionsTraining++; totalTraining += training; }

      const emergency = Number(data.emergency) || 0;
      if (emergency > 0) { regionsEmergency++; totalEmergency += emergency; }

      const sso = Number(data.studentTeams) || 0;
      if (sso > 0) { regionsSSO++; totalSSO += sso; }

      const mo = Number(data.localDepts) || 0;
      if (mo > 0) { regionsMO++; totalMO += mo; }

      const certified = Number(data.certified) || 0;
      if (certified > 0) { regionsCertified++; totalCertified += certified; }
    });

    return {
      human: { regions: regionsHuman, total: totalHuman },
      training: { regions: regionsTraining, total: totalTraining },
      emergency: { regions: regionsEmergency, total: totalEmergency },
      sso: { regions: regionsSSO, total: totalSSO },
      mo: { regions: regionsMO, total: totalMO },
      certified: { regions: regionsCertified, total: totalCertified },
    };
  }, [regionData]);

  const displayRegion = selectedRegion ?? hoveredRegion;

  const openEditModal = () => {
    if (selectedRegion) {
      setCurrentEditingRegion(selectedRegion.isoCode);
      setIsEditModalOpen(true);
    }
  };

  const handleSave = async (savedFormData: RegionData, savedPhotoFile: File | null) => {
    if (!currentEditingRegion) return;

    try {
      let photoUrl = savedFormData.photoUrl || "";
      if (savedPhotoFile) {
        const storageReference = storageRef(storage, `regions/${currentEditingRegion}/leader.jpg`);
        await uploadBytes(storageReference, savedPhotoFile);
        photoUrl = await getDownloadURL(storageReference);
      }

      const newData = { ...savedFormData, photoUrl };
      await setDoc(doc(db, "regions", currentEditingRegion), newData);
    } catch (err) {
      console.error("Save error:", err);
      alert("Ошибка сохранения. Проверьте права доступа.");
    }
  };

  const handleExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = e.target?.result as ArrayBuffer;
        const workbook = XLSX.read(data, { type: "array", cellDates: true });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(sheet);

        const updatePromises = jsonData.map(async (row) => {
          const isoCode = row.isoCode?.toString().trim();
          if (!isoCode || !regionsData[isoCode]) return;

          const updateData: Partial<RegionData> = {
            leaderName: row.leaderName || undefined,
            phone: row.phone || undefined,
            tg: row.tg || undefined,
            volunteers: row.volunteers ? Number(row.volunteers) : undefined,
            studentTeams: row.studentTeams ? Number(row.studentTeams) : undefined,
            localDepts: row.localDepts ? Number(row.localDepts) : undefined,
            humanMission: row.humanMission ? Number(row.humanMission) : undefined,
            training: row.training ? Number(row.training) : undefined,
            emergency: row.emergency ? Number(row.emergency) : undefined,
            certified: row.certified ? Number(row.certified) : undefined,
          };

          const dateFields: (keyof RegionData)[] = ["birthDate", "appointDate", "legalDate", "deptDate"];
          dateFields.forEach((field) => {
            let val = row[field];
            if (val instanceof Date) {
              updateData[field] = val.toISOString().split("T")[0];
            } else if (typeof val === "number") {
              const d = XLSX.SSF.parse_date_code(val);
              if (d) {
                updateData[field] = `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
              }
            } else if (typeof val === "string" && val.match(/^\d{1,2}\.\d{1,2}\.\d{4}$/)) {
              const [d, m, y] = val.split(".");
              updateData[field] = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
            }
          });

          Object.keys(updateData).forEach((key) => {
            if (updateData[key as keyof RegionData] === undefined) {
              delete updateData[key as keyof RegionData];
            }
          });

          if (Object.keys(updateData).length > 0) {
            await setDoc(doc(db, "regions", isoCode), updateData, { merge: true });
          }
        });

        await Promise.all(updatePromises);
        alert("Данные из Excel успешно загружены!");
      } catch (err) {
        console.error(err);
        alert("Ошибка загрузки Excel");
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleCardToggle = (cardKey: string) => {
    setActiveCards(prev =>
      prev.includes(cardKey)
        ? prev.filter(k => k !== cardKey)
        : [...prev, cardKey]
    );
  };

  const handleSearchSelect = (isoCode: string) => {
    const upperIso = isoCode.toUpperCase();
    const svgId = upperIso; // НЕ добавляем RU-! Ключ уже RU-VOR

    const info = regionsData[upperIso];

    console.log("Поиск: выбран регион", info?.name, "isoCode:", upperIso, "svgId:", svgId);

    if (!info) {
      console.warn("Регион не найден в regionsData:", upperIso);
      return;
    }

    // Показываем карточку и подсветку сразу
    setSelectedRegion(info);
    setSearchTerm(info.name);
    setShowDropdown(false);

    // Даём React время обновить SVG
    setTimeout(() => {
      if (triggerZoom.current) {
        console.log("Запускаем зум на", svgId);
        triggerZoom.current(svgId);
      } else {
        console.warn("Callback зума не установлен");
      }
    }, 0);
  };

  const filteredRegions = Object.entries(regionsData)
    .filter(([, info]) => info.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .slice(0, 10);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && filteredRegions.length === 1) {
      const [isoCode] = filteredRegions[0];
      handleSearchSelect(isoCode);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 text-white text-2xl">
        Загрузка данных из Firestore...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 text-red-500 text-2xl">
        {error}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 p-2 md:p-4">
      <div className="w-full max-w-[98vw] mx-auto">
        <div className="bg-slate-900/80 backdrop-blur-2xl border border-purple-800 rounded-3xl overflow-hidden shadow-2xl">
          <div className="p-4 md:p-8">
            <div className="mb-8 text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white bg-gradient-to-r from-purple-400 via-pink-400 to-indigo-400 bg-clip-text text-transparent">
                Карта РФ
              </h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
              <MapContainer
                handleMouseEnter={handleMouseEnter}
                handleMouseLeave={handleMouseLeave}
                handleClick={handleClick}
                selectedRegion={selectedRegion}
                hoveredRegion={hoveredRegion}
                regionData={regionData}
                activeCards={activeCards}
                zoom={zoom}
                setZoom={setZoom}
                onBackgroundClick={onBackgroundClick}
                onSearchZoom={(fn) => (triggerZoom.current = fn)}
              />

              <div className="bg-slate-900/90 backdrop-blur-xl rounded-3xl p-4 md:p-6 border border-purple-700/50 shadow-2xl flex flex-col">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl md:text-2xl font-bold text-purple-300">Информация о регионе</h3>
                  <ExcelImportButton
                    canShow={canEditRegion && isPavlik}
                    onFileChange={handleExcelUpload}
                  />
                </div>

                <div className="relative mb-4" ref={searchRef}>
                  <div className="flex items-center">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowDropdown(e.target.value.length > 0);
                      }}
                      onKeyDown={handleKeyDown}
                      placeholder="Поиск региона (например, Вор...)"
                      className="w-full bg-slate-800/70 text-white placeholder-purple-300/70 border border-purple-600 rounded-xl p-3 focus:outline-none focus:border-pink-500 transition-all"
                    />
                    <button
                      onClick={() => {
                        if (filteredRegions.length === 1) {
                          const [isoCode] = filteredRegions[0];
                          handleSearchSelect(isoCode);
                        }
                      }}
                      className="ml-2 bg-pink-500 text-white rounded-xl p-3 hover:bg-pink-600 transition-all disabled:opacity-50"
                      disabled={filteredRegions.length !== 1}
                    >
                      ✓
                    </button>
                  </div>

                  {showDropdown && filteredRegions.length > 0 && (
                    <div className="absolute z-10 w-full bg-slate-800/90 border border-purple-600 rounded-xl mt-1 max-h-60 overflow-y-auto shadow-lg">
                      {filteredRegions.map(([isoCode, info]) => (
                        <button
                          key={isoCode}
                          onClick={() => handleSearchSelect(isoCode)}
                          className="w-full text-left text-white p-3 hover:bg-purple-700/50 transition-all"
                        >
                          {info.name}
                        </button>
                      ))}
                    </div>
                  )}

                  {showDropdown && filteredRegions.length === 0 && searchTerm.length > 0 && (
                    <div className="absolute z-10 w-full bg-slate-800/90 border border-purple-600 rounded-xl mt-1 p-3 text-white">
                      Ничего не найдено
                    </div>
                  )}
                </div>

                <RegionInfoPanel
                  displayRegion={displayRegion}
                  regionData={regionData}
                  canEditRegion={canEditRegion}
                  selectedRegion={selectedRegion}
                  onEditClick={openEditModal}
                  formatDate={formatDate}
                />
              </div>
            </div>

            <StatsCards
              stats={stats}
              activeCards={activeCards}
              onCardToggle={handleCardToggle}
            />
          </div>
        </div>
      </div>

      <EditRegionModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        region={selectedRegion}
        currentData={regionData[currentEditingRegion || ""] || {}}
        onSave={handleSave}
      />
    </div>
  );
}